import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Snippet } from "@heroui/snippet";
import { Chip } from "@heroui/chip";
import { Card, CardBody, CardFooter, CardHeader } from "@heroui/card";
import { Button } from "@heroui/button";
import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { formatDistanceToNow } from "date-fns";

interface RequestItemProps {
  gn: string;
  type: string;
  status: "pending" | "approved" | "rejected";
  timestamp: string;
  animalData?: any;
  onOpen: () => void;
  setShowRequests: (value: boolean) => void;
}

export const RequestItem: React.FC<RequestItemProps> = ({
  gn,
  type,
  status,
  timestamp,
  animalData,
  onOpen,
  setShowRequests,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-expect-error
  const seconds = timestamp.seconds;
  const date = new Date(seconds * 1000);

  const timeAgo = formatDistanceToNow(date, { addSuffix: true });

  const statusColors = {
    pending: "bg-warning ",
    approved: "bg-successful text-green-800",
    rejected: "bg-danger text-red-800",
  };

  const handleTrigger = () => {
    onOpen();
    setShowRequests(false);
  };

  return (
    <motion.div
      layout
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      initial={{ opacity: 0, y: 20 }}
      transition={{ duration: 0.3 }}
    >
      <Card
        className="w-full dark:bg-slate-950 overflow-hidden shadow-lg shadow-blue-100
        dark:shadow-blue-950 hover:shadow-lg transition-shadow duration-300"
        radius={"sm"}
      >
        <CardHeader className={"w-full flex flex-row justify-between"}>
          <h3 className="text-xl font-semibold text-gray-600 dark:text-gray-300 ">
            {type} Request
          </h3>
          <Chip
            className={`${statusColors[status]} text-gray-900 font-bold text-xs`}
            radius={"sm"}
            size={"sm"}
            variant="solid"
          >
            {status}
          </Chip>
          <p className={"text-xs"}>{timeAgo}</p>
        </CardHeader>
        <CardBody className="w-full">
          <div className="flex justify-between items-center ">
            <Snippet
              fullWidth
              className={"bg-slate-100 dark:bg-gray-800"}
              content={gn}
              size={"sm"}
              symbol={"GN #"}
              variant={"shadow"}
            >
              {gn}
            </Snippet>
          </div>

          <AnimatePresence>
            {isExpanded && (
              <motion.div
                animate={{ opacity: 1, height: "auto" }}
                className="mt-4 pt-2 border-t border-gray-200"
                exit={{ opacity: 0, height: 0 }}
                initial={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.3 }}
              >
                <h3
                  className={
                    "font-semibold text-center" +
                    " text-primary-500 underline pb-4"
                  }
                >
                  Details Requested To Be Changed
                </h3>

                <div className="grid grid-cols-2 gap-4 text-sm text-gray-600 dark:text-gray-300">
                  <p>
                    <span className="font-medium">Breed:</span>{" "}
                    {animalData?.racialComposition}
                  </p>
                  <p>
                    <span className="font-medium">Sex:</span> {animalData?.sex}
                  </p>
                  <p>
                    <span className="font-medium">Age:</span> {animalData?.age}
                  </p>
                  <p>
                    <span className="font-medium">Birth Date:</span>{" "}
                    {animalData?.birthDate}
                  </p>
                  <p>
                    <span className="font-medium">Weight:</span>{" "}
                    {animalData?.weight} kg
                  </p>
                  <p>
                    <span className="font-medium">Size:</span>{" "}
                    {animalData?.size}
                  </p>
                  <p>
                    <span className="font-medium">Coat Color:</span>{" "}
                    {animalData?.coatColor}
                  </p>
                  <p>
                    <span className="font-medium">Breeding Type:</span>{" "}
                    {animalData?.breedingType}
                  </p>
                  <p>
                    <span className="font-medium">ADG:</span> {animalData?.adg}
                  </p>
                  <p>
                    <span className="font-medium">Dam:</span>{" "}
                    {animalData?.damName} ({animalData?.damBreed})
                  </p>
                  <p>
                    <span className="font-medium">Sire:</span>{" "}
                    {animalData?.sireName} ({animalData?.sireBreed})
                  </p>
                  <p className="col-span-2">
                    <span className="font-medium">Animal Status:</span>{" "}
                    {animalData?.animalStatus}
                  </p>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </CardBody>
        <CardFooter className="flex justify-end space-x-3 ">
          {/*<Button*/}
          {/*		variant="solid"*/}
          {/*		size="sm"*/}
          {/*		color={ 'danger' }*/}
          {/*		onClick={ () => onReject ( id ) }*/}
          {/*		className="text-green-100 border-green-200 hover:bg-danger-200 hover:text-green-200 transition-colors duration-300"*/}
          {/*>*/}
          {/*	<X size={ 16 } className="mr-2" />*/}
          {/*	Reject*/}
          {/*</Button >*/}

          <Button
            fullWidth
            className="text-green-800 bg-blue-100 dark:bg-blue-950 dark:text-gray-200 border-none  hover:bg-primary-800 hover:dark:bg-primary-800 hover:text-green-200 transition-colors duration-300"
            color={"primary"}
            size="sm"
            variant="faded"
            onPress={() => handleTrigger()}
          >
            <Check className="mr-2" size={16} />
            View Request
          </Button>
          <Button
            className="text-gray-500 hover:text-gray-700"
            size="sm"
            variant="light"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};
